import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { TenantService } from '../../../core/tenant/tenant.service';
import type {
  AssignedCommentItem,
  AssignedCommentsFilterOption,
  AssignedCommentsReadFilter,
} from '../models/assigned-comments.models';
import { AssignedCommentsApiService } from '../services/assigned-comments-api.service';
import { ExternalRepliesStore } from './external-replies.store';

@Injectable({ providedIn: 'root' })
export class AssignedCommentsStore {
  private readonly tenant = inject(TenantService);
  private readonly api = inject(AssignedCommentsApiService);
  private readonly externalReplies = inject(ExternalRepliesStore);
  private readonly loadedCompanyIdSignal = signal<string | null>(null);

  private readonly itemsSignal = signal<AssignedCommentItem[]>([]);
  private readonly workflowOptionsSignal = signal<AssignedCommentsFilterOption[]>([]);
  private readonly projectOptionsSignal = signal<AssignedCommentsFilterOption[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadingMoreSignal = signal(false);
  private readonly actionLoadingMapSignal = signal<Record<string, boolean>>({});
  private readonly errorSignal = signal<string | null>(null);
  private readonly unreadCountSignal = signal(0);
  private readonly pageSignal = signal(1);
  private readonly pageSizeSignal = signal(20);
  private readonly totalCountSignal = signal(0);
  private readonly selectedReadFilterSignal = signal<AssignedCommentsReadFilter>('all');
  private readonly selectedWorkflowIdSignal = signal<string | null>(null);
  private readonly selectedProjectIdSignal = signal<string | null>(null);
  private readonly searchTermSignal = signal('');

  readonly items = this.itemsSignal.asReadonly();
  readonly workflowOptions = this.workflowOptionsSignal.asReadonly();
  readonly projectOptions = this.projectOptionsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly actionLoadingMap = this.actionLoadingMapSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly unreadCount = this.unreadCountSignal.asReadonly();
  readonly page = this.pageSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly hasMore = computed(() => this.items().length < this.totalCount());
  readonly selectedReadFilter = this.selectedReadFilterSignal.asReadonly();
  readonly selectedWorkflowId = this.selectedWorkflowIdSignal.asReadonly();
  readonly selectedProjectId = this.selectedProjectIdSignal.asReadonly();
  readonly searchTerm = this.searchTermSignal.asReadonly();

  readonly isEmpty = computed(() => !this.loading() && !this.error() && this.items().length === 0);
  readonly hasUnread = computed(() => this.unreadCount() > 0);
  readonly displayedItems = computed(() => this.items());

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.loadedCompanyIdSignal.set(null);
        this.reset();
        return;
      }

      if (this.loadedCompanyIdSignal() !== companyId) {
        this.loadedCompanyIdSignal.set(companyId);
        this.reset();
        void this.loadUnreadCount();
      }
    });
  }

  async loadAssignedComments(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    this.pageSignal.set(1);
    try {
      const result = await this.api.getAssignedComments({
        page: 1,
        pageSize: this.pageSize(),
        readFilter: this.selectedReadFilter(),
        workflowId: this.selectedWorkflowId(),
        projectId: this.selectedProjectId(),
        search: this.searchTerm(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;

      this.itemsSignal.set(result.items);
      this.totalCountSignal.set(result.totalCount);
      this.workflowOptionsSignal.set(result.workflowOptions ?? []);
      this.projectOptionsSignal.set(result.projectOptions ?? []);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.set([]);
      this.totalCountSignal.set(0);
      this.errorSignal.set('assignedComments.states.error');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingSignal.set(false);
    }
  }

  async reloadAssignedComments(): Promise<void> {
    await Promise.all([this.loadAssignedComments(), this.loadUnreadCount()]);
  }

  async loadUnreadCount(): Promise<void> {
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    try {
      const result = await this.api.getUnreadCount();
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(result.count);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.unreadCountSignal.set(0);
    }
  }

  async markAsRead(id: string): Promise<void> {
    if (this.actionLoadingMap()[id]) return;
    this.setActionLoading(id, true);
    try {
      await this.api.markAsRead(id);
      this.externalReplies.onRead(id);
      this.itemsSignal.update((items) =>
        items.map((item) => (item.id === id ? { ...item, isRead: true } : item))
      );
      this.unreadCountSignal.update((count) => Math.max(count - 1, 0));
    } catch {
      this.errorSignal.set('assignedComments.actions.markReadError');
    } finally {
      this.setActionLoading(id, false);
    }
  }

  async markAsUnread(id: string): Promise<void> {
    if (this.actionLoadingMap()[id]) return;
    this.setActionLoading(id, true);
    try {
      await this.api.markAsUnread(id);
      void this.externalReplies.refresh();
      this.itemsSignal.update((items) =>
        items.map((item) => (item.id === id ? { ...item, isRead: false } : item))
      );
      this.unreadCountSignal.update((count) => count + 1);
    } catch {
      this.errorSignal.set('assignedComments.actions.markUnreadError');
    } finally {
      this.setActionLoading(id, false);
    }
  }

  async changeReadFilter(filter: AssignedCommentsReadFilter): Promise<void> {
    if (this.selectedReadFilter() === filter) return;
    this.selectedReadFilterSignal.set(filter);
    await this.reloadAssignedComments();
  }

  async changeWorkflowFilter(value: string | null): Promise<void> {
    if (this.selectedWorkflowId() === value) return;
    this.selectedWorkflowIdSignal.set(value);
    await this.reloadAssignedComments();
  }

  async changeProjectFilter(value: string | null): Promise<void> {
    if (this.selectedProjectId() === value) return;
    this.selectedProjectIdSignal.set(value);
    await this.reloadAssignedComments();
  }

  async changeSearch(value: string): Promise<void> {
    if (this.searchTerm() === value) return;
    this.searchTermSignal.set(value);
    await this.reloadAssignedComments();
  }

  async loadNextPage(): Promise<void> {
    if (!this.hasMore() || this.loadingMore()) return;
    const requestCompanyId = this.tenant.companyId();
    if (!requestCompanyId) return;

    this.loadingMoreSignal.set(true);
    const nextPage = this.page() + 1;
    try {
      const result = await this.api.getAssignedComments({
        page: nextPage,
        pageSize: this.pageSize(),
        readFilter: this.selectedReadFilter(),
        workflowId: this.selectedWorkflowId(),
        projectId: this.selectedProjectId(),
        search: this.searchTerm(),
      });
      if (!this.isSameCompany(requestCompanyId)) return;
      this.itemsSignal.update((current) => [...current, ...result.items]);
      this.pageSignal.set(nextPage);
      this.totalCountSignal.set(result.totalCount);
    } catch {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.errorSignal.set('assignedComments.states.errorLoadMore');
    } finally {
      if (!this.isSameCompany(requestCompanyId)) return;
      this.loadingMoreSignal.set(false);
    }
  }

  reset(): void {
    this.itemsSignal.set([]);
    this.workflowOptionsSignal.set([]);
    this.projectOptionsSignal.set([]);
    this.loadingSignal.set(false);
    this.loadingMoreSignal.set(false);
    this.actionLoadingMapSignal.set({});
    this.errorSignal.set(null);
    this.unreadCountSignal.set(0);
    this.pageSignal.set(1);
    this.pageSizeSignal.set(20);
    this.totalCountSignal.set(0);
    this.selectedReadFilterSignal.set('all');
    this.selectedWorkflowIdSignal.set(null);
    this.selectedProjectIdSignal.set(null);
    this.searchTermSignal.set('');
  }

  private isSameCompany(requestCompanyId: string): boolean {
    return this.tenant.companyId() === requestCompanyId;
  }

  private setActionLoading(id: string, loading: boolean): void {
    this.actionLoadingMapSignal.update((current) => ({
      ...current,
      [id]: loading,
    }));
  }
}
